export interface FeatureDefaultValue {
    /** Identifier of the feature.*/
    id: FeatureId;

    /** Default value of the feature. Null or undefined is interpreted as false.
     * This can be set by saving a feature default value in the database.
     */
    defaultValue?: boolean;

    /** Last time this value was updated */
    lastUpdated?: Date;
}

export interface Feature extends FeatureDefaultValue {
    /** Name of the feature */
    name: string;

    /** Description of the feature */
    description: string;

    /**
     * True if this feature does nothing in the codebase
     * and is preserved for backward compatibility.
     */
    removed?: boolean;

    /**
     * True if this feature is for automated testing purposes only.
     */
    hidden?: boolean;
}

export enum FeatureId {
    /** Example feature for testing. */
    HelloWorld = 'hello-world',
    /** Enables the iHeartRadio integration on walt */
    IHeartRadioIntegration = 'iheartradio-integration',
    /** Personal Gas Monitors. */
    GasMonitors = 'gas-monitors',
    /** Report safety observations. */
    SafetyObservation = 'safety-observation',
    /** Switch walt to offline radio in an outage. */
    WaltOfflineMode = 'walt-offline-mode',
    /** Automatically switch walt to offline mode when network or mqtt is unavailable for a period of time. */
    WaltOfflineModeNetworkInitiated = 'walt-offline-mode-network-initiated',
    /** Enables walt users to trigger voice assistant */
    AiVoiceAssistant = 'ai-voice-assistant',
    /** Enables mobile PTT button to act like walt */
    MobilePttButton = 'mobile-ptt-button',
    /** Routes live PTT audio through the ptt-service micro-service instead of the monolith */
    PttIsolation = 'ptt-isolation',
    /** Only allows users to be logged into a single walt at a time */
    SingleWaltLogin = 'single-walt-login',
    /** Enables users to change sim settings for sites via console */
    ConsoleSimSettings = 'console-sim-settings',
    /**
     * Sync in native Android.
     */
    WaltNativeSync = 'walt-native-sync',
    /** Enables the new menu layout */
    NewMenu = 'walt-new-menu',
    /**
     * Enables the new channel creation flow in walt
     */
    NewChannelFlow = 'walt-new-channel-flow',
    /** Hatch Speaker Mic */
    HatchSpeakerMic = 'hatch-speaker-mic',
    /** Enables users to access the mesh deployment tool UI */
    MeshDeploymentTool = 'mesh-deployment-tool',
    /** Enables the rollout of new enhancements available for Walt 2 Device Management */
    Walt2DeviceManagementEnhancements = 'walt2-device-management-enhancements',
    /**
     * Retired: walt credentials are always enforced and current builds ignore this flag.
     * Kept only so older walt and Console builds in the field still read it as enabled.
     */
    WaltCredentialEnforced = 'walt-credential-enforced',
    /**
     * When enabled, the volume of panic alerts will defer to the current volume setting rather than maximum.
     */
    PanicVolumeControl = 'panic-volume-control',
    /**
     * Enables the translator feature in walt
     */
    WaltTranslator = 'walt-translator',
    /**
     * Lets the walt edge translator keep working with no connection, transcribing and translating on device
     */
    WaltTranslatorOffline = 'walt-translator-offline',
    /**
     * Enables the Android PTT Framework on the mobile app
     */
    AndroidPttFramework = 'android-ptt-framework',
    /**
     * Enables new sharing users across accounts flow for given sites
     */
    ShareUsersAcrossAccounts = 'share-users-across-accounts',
    /**
     * Enables the NWS (National Weather Service) weather alerts workflow trigger
     */
    NwsWeatherAlerts = 'nws-weather-alerts',
    /**
     * Log in to walt without internet connectivity. Requires "Native Sync" and "Walt Credential Enforced" to be enabled.
     */
    OfflineLogin = 'offline-login',
    /**
     * Enables the network scheduler to toggle airplane mode on and off when the device is in a poor network state.
     */
    EnableNetSchedulerAirplaneToggle = 'enable-net-scheduler-airplane-toggle',
    /**
     * When enabled, a walt 5G turns its mesh radios off while docked on a wrangler and back on when undocked.
     */
    DisableMeshWhenDocked = 'disable-mesh-when-docked',
    /**
     * When enabled, a walt 2 counts docks where it never read the wrangler's NFC tag, reports each miss, and warns on the login screen after 3 in a row.
     */
    WaltNfcReaderCheck = 'walt-nfc-reader-check',
    /**
     * Routes Mongo change events through RabbitMQ instead of dispatching them in-process.
     *
     * Backend-only infrastructure flag: it changes how change-stream consumers are invoked, not anything
     * a client can see. Facility-level flags are ignored — a database write is not facility-scoped.
     * Routes Mongo change events through RabbitMQ instead of dispatching in-process. Backend-only, invisible to
     * clients, and facility-level flags are ignored since a write is not facility-scoped.
     */
    RabbitMqChangeStream = 'rabbitmq-change-stream',
    /**
     * Gates the safety feature set: panic button mode, silent alerts and per-alert-type
     * volume, hard fall detection, and lone worker inactivity monitoring.
     */
    SafetyEnhancements = 'safety-enhancements',
    /**
     * Distributes walt firmware updates over the DECT mesh -- gossiping versions and flooding update
     * bytes -- instead of every device downloading its own. Off by default, because flooding occupies
     * the shared mesh channel. Scoped to DECT: the bluetooth and wifi-direct transports are
     * point-to-point and keep working either way.
     */
    MeshOta = 'mesh-ota',
    /**
     * Resends a walt's mesh outbox with new sequences so relays that heard the original still upload it, and has
     * peers relay those delayed PTT resends without playing them. Off keeps the old resend, which relays could drop.
     */
    MeshOutboxReplayAsNew = 'mesh-outbox-replay-as-new',
    /**
     * Allows a walt 5G to run its DECT and LoRa mesh radios at the site it is assigned to.
     * Off turns the radios off and hides the user's Mesh tile. Applied only while the walt is online.
     */
    Mesh = 'walt-mesh',
    /**
     * Transcribe PTT audio through the AI cascade on the Azure Foundry transcription API.
     *
     * Covers BOTH cascade engines: MAI where it can hear the language, and Azure's own models
     * behind it for the six it cannot and as the retry when it fails. There is no separate flag for
     * the second, so this turns the whole cascade on or off.
     *
     * Off is today's legacy behavior exactly. A clip the cascade cannot serve -- a language neither
     * engine covers, an open circuit, a failure -- falls back to legacy regardless, so the flag
     * turns the cascade on rather than turning legacy off.
     */
    MicrosoftAiTranscription = 'microsoft-ai-transcription',
    /** Powers the walt LoRa radio off for the account. Off (the default) leaves LoRa on, including its relay of PTT into DECT. */
    DisableLora = 'disable-lora',
    /**
     * Serves DVR and person badge history for dropped months from the parquet archive, and lets the archive
     * drop the account's archived months from Mongo. Account-level; once months are dropped, off hides them.
     */
    BadgeHistoryArchive = 'badge-history-archive',
    /** Detect which of a bilingual user's primary and secondary languages was spoken or typed. */
    BilingualSpeech = 'bilingual-speech',
    /**
     * Moves an account's mobile and walt apps onto EMQX, or every account's through the platform default; the backend runs
     * EMQX beside Aedes while it is on anywhere. Account-level only; facility flags are ignored.
     */
    EmqxBroker = 'emqx-broker',
    /** Lets users pick the voice (Voice 1 male, Voice 2 female) others hear when their messages are read aloud. */
    MaleFemaleVoices = 'male-female-voices',
    /** Hides dialect selection and transcribes every speaker in their language's default dialect; saved dialects are kept. */
    RemoveDialects = 'remove-dialects',
    /** Lets people use the Weavix Desktop App; off shows a notice and clears its live data. */
    DesktopRadio = 'desktop-radio',
    /**
     * Requires the caller's group to grant the action behind each `/track/events` location history read.
     * Account-level; off leaves those reads unchecked, as before.
     */
    StrictLocationReadPermissions = 'strict-location-read-permissions',
    /**
     * Resolves an account's entitlements from the packages assigned to its sites instead of its Products.
     * Account-level; off keeps today's Product-based entitlements.
     */
    SiteScopedPackages = 'site-scoped-packages',
    /** Lets any client search the messages, files, links, channels, people and crafts the user can see. Account-level only. */
    GlobalSearch = 'global-search',
    /**
     * Spreads AI transcription round robin across every regional Foundry account, skipping a region while it fails.
     * Off sends every clip to the single primary account. Only matters where `MicrosoftAiTranscription` is on.
     */
    MicrosoftAiRegionPool = 'microsoft-ai-region-pool',
}

/**
 * Id of the hotspot feature flag the package migration (WV-4444) turns on for accounts holding the hotspot product.
 * The flag itself (WV-5348) must be declared in `FeatureId` with this value; until then its rows exist but nothing reads them.
 */
export const HOTSPOT_FEATURE_FLAG_ID = 'hotspot';

export const Features: { [key in FeatureId]: Feature } = {
    [FeatureId.HelloWorld]: {
        id: FeatureId.HelloWorld,
        name: 'Hello World',
        description: 'I hope you are having a great day!',
        hidden: true,
    },
    [FeatureId.IHeartRadioIntegration]: {
        id: FeatureId.IHeartRadioIntegration,
        name: 'iHeartRadio Integration',
        description: 'Enables the iHeartRadio integration on walt',
    },
    [FeatureId.GasMonitors]: {
        id: FeatureId.GasMonitors,
        name: 'Gas Monitors',
        description: 'Personal Gas Monitors.',
    },
    [FeatureId.SafetyObservation]: {
        id: FeatureId.SafetyObservation,
        name: 'Safety Observation',
        description: 'Report safety observations.',
    },
    [FeatureId.WaltOfflineMode]: {
        id: FeatureId.WaltOfflineMode,
        name: 'Offline Mode',
        description: 'Switch walt to offline radio in an outage.',
    },
    [FeatureId.WaltOfflineModeNetworkInitiated]: {
        id: FeatureId.WaltOfflineModeNetworkInitiated,
        name: 'Offline Mode Network Initiated',
        description: 'Automatically switch walt to offline mode when network or mqtt is unavailable for a period of time.',
    },
    [FeatureId.AiVoiceAssistant]: {
        id: FeatureId.AiVoiceAssistant,
        name: 'AI Voice Assistant',
        description: 'Enables walt users to trigger and use voice assistant',
    },
    [FeatureId.MobilePttButton]: {
        id: FeatureId.MobilePttButton,
        name: 'Mobile PTT Button',
        description: 'Enables mobile PTT button to act like walt',
    },
    [FeatureId.PttIsolation]: {
        id: FeatureId.PttIsolation,
        name: 'PTT Isolation',
        description: 'Routes live PTT audio through the ptt-service micro-service instead of the monolith',
        defaultValue: false,
    },
    [FeatureId.SingleWaltLogin]: {
        id: FeatureId.SingleWaltLogin,
        name: 'Single Walt Login',
        description: 'Only allows users to be logged into a single walt at a time',
    },
    [FeatureId.ConsoleSimSettings]: {
        id: FeatureId.ConsoleSimSettings,
        name: 'Console SIM Settings',
        description: 'Enables users to change sim settings for sites via console',
    },
    [FeatureId.WaltNativeSync]: {
        id: FeatureId.WaltNativeSync,
        name: 'walt Native Sync',
        description: 'Improve performance of walt by running sync in native code.',
    },
    [FeatureId.NewMenu]: {
        id: FeatureId.NewMenu,
        name: 'walt New Menu',
        description: 'Enables the new menu layout',
        defaultValue: false,
    },
    [FeatureId.NewChannelFlow]: {
        id: FeatureId.NewChannelFlow,
        name: 'walt New Channel Flow',
        description: 'Enables the new channel creation flow in walt',
        defaultValue: false,
    },
    [FeatureId.HatchSpeakerMic]: {
        id: FeatureId.HatchSpeakerMic,
        name: 'Hatch Speaker Mic',
        description: 'Enables the Hatch Speaker Mic integration',
        defaultValue: false,
    },
    [FeatureId.MeshDeploymentTool]: {
        id: FeatureId.MeshDeploymentTool,
        name: 'Mesh Deployment Tool',
        description: 'Enables users to access the mesh deployment tool UI',
        defaultValue: false,
    },
    [FeatureId.Walt2DeviceManagementEnhancements]: {
        id: FeatureId.Walt2DeviceManagementEnhancements,
        name: 'Walt 2 Device Management Enhancements',
        description: 'Enables the rollout of new enhancements available for Walt 2 Device Management',
        defaultValue: false,
    },
    [FeatureId.WaltCredentialEnforced]: {
        id: FeatureId.WaltCredentialEnforced,
        name: 'Walt Credential Enforced',
        description: 'Always enforced; current backend, walt and Console builds no longer read it. Kept for older walt and Console builds still in the field.',
        defaultValue: true,
    },
    [FeatureId.PanicVolumeControl]: {
        id: FeatureId.PanicVolumeControl,
        name: 'Panic Volume Control',
        description: 'When enabled, the volume of panic alerts will defer to the current volume setting rather than maximum.',
        defaultValue: false,
    },
    [FeatureId.WaltTranslator]: {
        id: FeatureId.WaltTranslator,
        name: 'Walt Translator',
        description: 'Enables the translator feature in walt',
        defaultValue: false,
    },
    [FeatureId.WaltTranslatorOffline]: {
        id: FeatureId.WaltTranslatorOffline,
        name: 'Walt Translator Offline',
        description: 'Lets the walt edge translator keep working with no connection, transcribing and translating on device.',
        defaultValue: false,
    },
    [FeatureId.AndroidPttFramework]: {
        id: FeatureId.AndroidPttFramework,
        name: 'Android PTT Framework',
        description: 'Enables the Android PTT Framework on the mobile app',
        defaultValue: false,
    },
    [FeatureId.ShareUsersAcrossAccounts]: {
        id: FeatureId.ShareUsersAcrossAccounts,
        name: 'Share Users Across Accounts',
        description: 'Enables new sharing users across accounts flow for given sites',
        defaultValue: false,
    },
    [FeatureId.NwsWeatherAlerts]: {
        id: FeatureId.NwsWeatherAlerts,
        name: 'NWS Weather Alerts',
        description: 'Enables the NWS (National Weather Service) weather alerts workflow trigger',
        defaultValue: false,
    },
    [FeatureId.OfflineLogin]: {
        id: FeatureId.OfflineLogin,
        name: 'Walt Offline Login',
        description: 'Log in to walt without internet connectivity. Requires "Native Sync" and "Walt Credential Enforced" to be enabled.',
        defaultValue: false,
    },
    [FeatureId.EnableNetSchedulerAirplaneToggle]: {
        id: FeatureId.EnableNetSchedulerAirplaneToggle,
        name: 'Enable Network Scheduler Airplane Toggle',
        description: 'Enables the network scheduler to toggle airplane mode on and off when the device is in a poor network state.',
        defaultValue: false,
    },
    [FeatureId.MeshOta]: {
        id: FeatureId.MeshOta,
        name: 'Mesh OTA',
        description: 'Distributes walt firmware updates peer-to-peer over the DECT mesh. Bluetooth and wifi-direct are unaffected.',
        defaultValue: false,
    },
    [FeatureId.MeshOutboxReplayAsNew]: {
        id: FeatureId.MeshOutboxReplayAsNew,
        name: 'Mesh Outbox Replay As New',
        description: 'Walts resend messages saved with no internet as new mesh packets so relays upload them, and do not play resent PTTs.',
        defaultValue: false,
    },
    [FeatureId.Mesh]: {
        id: FeatureId.Mesh,
        name: 'Mesh',
        description: 'Allows walt 5G radios at the site to use the DECT and LoRa mesh. Off turns the mesh radios off and hides the Mesh setting. Applied when the walt is online.',
        defaultValue: true,
    },
    [FeatureId.DisableMeshWhenDocked]: {
        id: FeatureId.DisableMeshWhenDocked,
        name: 'Disable Mesh When Docked',
        description: 'Turns a walt 5G\'s mesh radios off while docked on a wrangler and back on when undocked',
        defaultValue: true,
    },
    [FeatureId.WaltNfcReaderCheck]: {
        id: FeatureId.WaltNfcReaderCheck,
        name: 'Walt NFC Reader Check',
        description: 'A walt 2 reports docks where it never read the wrangler\'s NFC tag, and shows a badge reader warning on the login screen after 3 in a row',
        defaultValue: false,
    },
    [FeatureId.RabbitMqChangeStream]: {
        id: FeatureId.RabbitMqChangeStream,
        name: 'RabbitMQ Change Stream',
        description: 'Publishes Mongo change events to RabbitMQ for asynchronous, fleet-wide processing. '
            + 'When off, change-stream consumers are invoked in-process by the server that made the write. '
            + 'Set the default value to control the global rollout and use account flags to opt individual '
            + 'accounts in or out. Facility-level flags have no effect.',
        defaultValue: false,
    },
    [FeatureId.MicrosoftAiTranscription]: {
        id: FeatureId.MicrosoftAiTranscription,
        name: 'Microsoft AI Transcription',
        description: 'Transcribes PTT audio through the AI cascade instead of the legacy Azure '
            + 'Speech recognizer. Microsoft\'s MAI model serves most languages; the six it cannot '
            + 'hear — Burmese, Albanian, Pashto, Lao, Amharic and Khmer — are served by Azure\'s own '
            + 'models on the same API, which also retries a clip MAI fails. Anything the cascade '
            + 'cannot serve still falls back to legacy. Off is the legacy Azure Speech behavior.',
        defaultValue: false,
    },
    [FeatureId.BadgeHistoryArchive]: {
        id: FeatureId.BadgeHistoryArchive,
        name: 'Badge History Archive',
        description: 'Serves DVR playback and person location history for months older than the '
            + 'current and previous month from the badgeUpdates parquet archive once they are dropped '
            + 'from Mongo, and reads every month of a range rather than only its first and last. '
            + 'Off reads Mongo only, exactly as before. Facility-level flags have no effect. '
            + 'Turning it on also lets the archive drop that account\'s archived months from Mongo; '
            + 'once any are dropped, turning it off hides them from DVR and history.',
        defaultValue: false,
    },
    [FeatureId.SafetyEnhancements]: {
        id: FeatureId.SafetyEnhancements,
        name: 'Safety Enhancements',
        description: 'Enables the safety feature set: panic button mode, silent alerts and per-alert-type volume, hard fall detection, and lone worker inactivity monitoring.',
        defaultValue: false,
    },
    [FeatureId.DisableLora]: {
        id: FeatureId.DisableLora,
        name: 'Disable LoRa',
        description: 'Powers the walt LoRa radio off, so walts mesh over DECT only and no longer relay PTT from walts beyond DECT range.',
        defaultValue: false,
    },
    [FeatureId.BilingualSpeech]: {
        id: FeatureId.BilingualSpeech,
        name: 'Bilingual Speech',
        description: 'Lets users set a secondary language, and detects which of their two languages '
            + 'was used when they talk on PTT or type a message. Off ignores the secondary language.',
        defaultValue: false,
    },
    [FeatureId.EmqxBroker]: {
        id: FeatureId.EmqxBroker,
        name: 'EMQX Broker',
        description: 'Moves this account\'s mobile and walt apps onto the EMQX broker; app versions that cannot use it stay '
            + 'on the current broker. A walt moves with its own account; a phone user moves only when every account they '
            + 'belong to is on. Account-level only.',
        defaultValue: false,
    },
    [FeatureId.MaleFemaleVoices]: {
        id: FeatureId.MaleFemaleVoices,
        name: 'Male/Female Voices',
        description: 'Lets users choose Voice 1 (male) or Voice 2 (female) for the text-to-speech others hear '
            + 'when their text or translated messages are read aloud. Off uses Voice 1 for everyone.',
        defaultValue: false,
    },
    [FeatureId.RemoveDialects]: {
        id: FeatureId.RemoveDialects,
        name: 'Remove Dialects',
        description: 'Hides dialect selection and transcribes everyone in their language\'s default dialect '
            + '(e.g. Spanish as es-MX). Saved dialects are kept, so turning this off restores them.',
        defaultValue: false,
    },
    [FeatureId.DesktopRadio]: {
        id: FeatureId.DesktopRadio,
        name: 'Desktop Radio',
        description: 'Lets people use the Weavix Desktop App. Off shows a notice after sign-in and clears its live data.',
        defaultValue: false,
    },
    [FeatureId.StrictLocationReadPermissions]: {
        id: FeatureId.StrictLocationReadPermissions,
        name: 'Strict Location Read Permissions',
        description: 'Refuses Live Look, backtrack, geofence history and walt session history to anyone whose '
            + 'group does not grant the matching action. Before turning it on, check that the account has the '
            + 'View Backtrack and View Geofence History entitlements, that every group that opens the Mayday map '
            + 'or backtrack has View Backtrack, and that groups reading geofence history have View Geofence History. '
            + 'Account-level only; '
            + 'facility settings have no effect. Off leaves these reads unchecked, as before.',
        defaultValue: false,
    },
    [FeatureId.SiteScopedPackages]: {
        id: FeatureId.SiteScopedPackages,
        name: 'Site-Scoped Packages',
        description: 'Decides what each site can use from the packages assigned to it, or the account default, '
            + 'instead of the account\'s Products. Account-level only. Off keeps today\'s Product-based entitlements.',
        defaultValue: false,
    },
    [FeatureId.GlobalSearch]: {
        id: FeatureId.GlobalSearch,
        name: 'Global Search',
        description: 'Lets clients search the messages, files, links, channels, people and crafts a user can see. Account-level only. '
            + 'Off refuses the search endpoint.',
        defaultValue: false,
    },
    [FeatureId.MicrosoftAiRegionPool]: {
        id: FeatureId.MicrosoftAiRegionPool,
        name: 'Microsoft AI Region Pool',
        description: 'Spreads Microsoft AI Transcription across every Azure region provisioned for it, taking turns, '
            + 'and skips a region while it is failing. Off sends every clip to the single primary region. '
            + 'Has no effect unless Microsoft AI Transcription is also on.',
        defaultValue: false,
    },
};
